// Module-level singleton WebSocket. Lazy-opens on first use, drops the
// reference on close so the next call opens a fresh socket. Shared by the
// query console and the live-watch store so both pin the same connection.

import { KineDB, KineDBError } from '@kinedb/client';
import {
  clearSession, getCredentials, getToken, hasAccess, isAnonymous, subscribe as subscribeSession,
} from './auth-store.js';

const db = new KineDB();
// The dial, a seam so the tests drive the lifecycle without a server.
let _dial = () => db.connect();
let _sock = null;
let _sockPromise = null;
const _onOpenListeners = new Set();

// Connection-state tracker. The StatusBar in the chrome subscribes to this
// to drive the connected/connecting/offline dot. State transitions:
//   offline → connecting on getSocket() entry
//   connecting → connected when the WS upgrade resolves
//   connected → offline on the WebSocket's `close` event
//   connecting → offline if `db.connect()` rejects
const STATUS = { OFFLINE: 'offline', CONNECTING: 'connecting', CONNECTED: 'connected' };
let _status = STATUS.OFFLINE;
const _statusListeners = new Set();

function setStatus(next) {
  if (_status === next) return;
  _status = next;
  for (const cb of _statusListeners) { try { cb(next); } catch { /* listener error */ } }
}

export function getStatus() { return _status; }
export function subscribeStatus(cb) {
  _statusListeners.add(cb);
  return () => _statusListeners.delete(cb);
}

// Background auto-reconnect. Without this, the StatusBar stays "Offline"
// forever after a server restart — Home.jsx polls /sql via HTTP, so nothing
// in the UI dials the WebSocket back up by itself. Exponential backoff:
// 1s, 2s, 4s, 8s, 16s, 30s cap. The attempt counter resets on every
// successful connect so a brief blip doesn't poison the next outage.
let _reconnectTimer = null;
let _reconnectAttempt = 0;

function scheduleReconnect() {
  if (_reconnectTimer) return;
  const delay = Math.min(30000, 1000 * Math.pow(2, _reconnectAttempt));
  _reconnectAttempt += 1;
  _reconnectTimer = setTimeout(() => {
    _reconnectTimer = null;
    // The catch path inside getSocket will schedule the next attempt.
    getSocket().catch(() => { /* see catch path */ });
  }, delay);
}

function cancelReconnect() {
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
  _reconnectAttempt = 0;
}

/**
 * B05 slice 7: why a failed WS login is not automatically a logout.
 *
 * `Response::Error` carries no error KIND on the wire — `from_kinedb_error` keeps
 * only the message string — so the text is all there is to go on. `wsSql` below
 * already sets that precedent.
 *
 * `refused` = the credential in memory is not one this server accepts (the
 * password changed, or the user was dropped). Retrying that forever is an
 * unexplainable silence: a status dot stuck on "connecting" and no reason
 * anywhere. `transient` = a timeout or a dropped socket, where the reconnect loop
 * is exactly the right answer and the HTTP bearer is still good. An unrecognised
 * failure defaults to `transient`, because logging a user out on a message nobody
 * anticipated is the more destructive of the two mistakes.
 *
 * B1237 moved the BODY to the dependency-free `./login-failure.js` and re-exports
 * it here, so every caller of this file and `socket-auth.test.js` are unchanged.
 * The four demo pages need the same answer about a dead token, and they cannot
 * import THIS file: its module graph pulls the console's auth store in.
 */
export { classifyLoginFailure } from './login-failure.js';
// The re-export above does not bind the name in THIS module's scope, and
// `authenticateAndAnnounce` below calls it.
import { classifyLoginFailure } from './login-failure.js';

/**
 * B05 slice 7: log in on a freshly-opened socket, THEN announce it.
 *
 * The order is load-bearing. `live-store.js` re-registers one `WATCH` per table
 * from `onSocketOpen` and `db-store.js` re-issues `USE <db>` from the same
 * callback; `WATCH t` is gated. Announcing first means every re-subscribe on
 * every reconnect goes out with no identity — and reconnects happen on their own
 * backoff, at moments nothing in the UI is watching.
 *
 * Returns whether the socket is fit to announce. Exported (with `listeners`
 * injected) so the ordering has a test that does not need a server.
 */
export async function authenticateAndAnnounce(sock, listeners, isCurrent = () => true) {
  const cred = getCredentials();
  if (cred) {
    try {
      // Token re-auth: the bearer `POST /login` minted, re-presented on every
      // socket. The password never reaches this file.
      await sock.authenticateWithToken(cred.token);
    } catch (e) {
      if (classifyLoginFailure(e) === 'refused') {
        // Drop the session so App's gate renders <Login/> with a real reason
        // instead of leaving the shell up over a socket that can do nothing.
        clearSession();
      }
      return false;
    }
  }
  // B1338: the session changed during the login, so this socket carries the old
  // identity (or none). The fan-out belongs to the socket of the new session.
  if (!isCurrent()) return false;
  // After the singleton is in place, so re-subscribe logic sees a live socket.
  for (const cb of listeners) { try { cb(sock); } catch { /* listener error */ } }
  return true;
}

/**
 * B1338: the socket follows the session.
 *
 * A socket authenticates ONCE, when it opens, with the credential of that moment.
 * So a socket belongs to one session, and a change of the session makes it wrong:
 * a stale token was refused and cleared, the backoff dialled again with no
 * credential, and that socket was announced as CONNECTED. The login that came
 * next re-used it, and every gated request on it failed with
 * CLIENT_AUTH_REQUIRED until an F5.
 *
 * `_gen` counts the sessions. A change of the session key (the token, or the
 * proven-anonymous plane) drops the socket and any dial in flight; a dial that
 * finishes under an older `_gen` closes its socket instead of installing it.
 */
let _gen = 0;
let _sessionKey = null;
let _unfollow = null;

function sessionKey() {
  return getToken() ?? (isAnonymous() ? 'anonymous' : null);
}

function dropSocket() {
  _gen += 1;
  cancelReconnect();
  const s = _sock;
  _sock = null;
  _sockPromise = null;
  setStatus(STATUS.OFFLINE);
  if (s) { try { s.close(); } catch { /* already closing */ } }
}

function followSession() {
  if (_unfollow) _unfollow();
  _sessionKey = sessionKey();
  _unfollow = subscribeSession(() => {
    const k = sessionKey();
    if (k === _sessionKey) return;
    _sessionKey = k;
    dropSocket();
    // Dial for the new session at once, so the status dot tells the truth.
    if (hasAccess()) getSocket().catch(() => { /* StatusBar shows offline */ });
  });
}
followSession();

export async function getSocket() {
  if (_sock && _sock.connected) return _sock;
  if (_sockPromise) return _sockPromise;
  // B1338: with no session and no proven-anonymous server the login form is up.
  // A socket dialled now has no identity, so there is nothing to connect.
  if (!hasAccess()) {
    setStatus(STATUS.OFFLINE);
    throw new KineDBError('no session: log in first');
  }
  const gen = _gen;
  const isCurrent = () => gen === _gen;
  setStatus(STATUS.CONNECTING);
  const p = (async () => {
    try {
      const s = await _dial();
      if (!isCurrent()) {
        try { s.close(); } catch { /* already closing */ }
        throw new KineDBError('the session changed during the dial');
      }
      s.ws.addEventListener('close', () => {
        if (_sock === s) {
          _sock = null;
          setStatus(STATUS.OFFLINE);
          scheduleReconnect();
        }
      });
      _sock = s;
      // B05 slice 7: authenticate BEFORE the fan-out (see above). A login this
      // socket cannot complete makes it useless for anything gated, so close it
      // and let the backoff bring up a fresh one rather than hand callers a
      // socket whose WATCHes will be denied.
      if (!(await authenticateAndAnnounce(s, _onOpenListeners, isCurrent))) {
        if (_sock === s) _sock = null;
        s.close();
        throw new KineDBError('WebSocket login failed');
      }
      setStatus(STATUS.CONNECTED);
      cancelReconnect();
      return s;
    } catch (e) {
      // A dial of an older session is not this session's failure: the new
      // session owns the status and the reconnect.
      if (isCurrent()) {
        setStatus(STATUS.OFFLINE);
        if (hasAccess()) scheduleReconnect();
      }
      throw e;
    } finally {
      if (_sockPromise === p) _sockPromise = null;
    }
  })();
  _sockPromise = p;
  return p;
}

// Pure WebSocket SQL with one reconnect retry on transient drops.
export async function wsSql(sql) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const s = await getSocket();
      return await s.sql(sql);
    } catch (e) {
      const isConnErr = e instanceof KineDBError &&
        /connection closed|WebSocket connection failed/i.test(e.message);
      if (attempt === 0 && isConnErr) { _sock = null; continue; }
      throw e;
    }
  }
}

/**
 * B2174: ONE complete transaction on the shared socket (`transaction()` of
 * `@kinedb/client`). The server commits all the statements or none of them.
 *
 * It does NOT send again after a dropped connection, as `wsSql` does: the
 * frame may have reached the server and committed. A caller that wants a
 * safe second send passes a `token` and calls again.
 */
export async function wsTransaction(statements, options = {}) {
  const s = await getSocket();
  return s.transaction(statements, options);
}

// Subscribe to socket-open events. Used by the live store to re-subscribe
// after a transparent reconnect.
export function onSocketOpen(cb) {
  _onOpenListeners.add(cb);
  return () => _onOpenListeners.delete(cb);
}

/**
 * B05 slice 7: tear the shared socket down on logout.
 *
 * All three steps are required, and the third is the one that is easy to miss:
 * without `cancelReconnect()` the backoff timer is still armed, so the socket
 * comes straight back up moments after the user logged out — and `cancelReconnect`
 * is module-private, which is why logout has to route through here rather than
 * calling `KineDBSocket.close()` itself.
 */
export function resetSocket() {
  // B1338: `dropSocket` also retires a dial in flight, so it cannot install
  // itself after the logout.
  dropSocket();
}

export { KineDBError };

/** Test-only: replace the dial (`db.connect()`) with a fake. */
export function __setDialForTests(fn) { _dial = fn; }

/** Test-only: drop the singleton's state without notifying listeners. */
export function __resetForTests() {
  cancelReconnect();
  _sock = null;
  _sockPromise = null;
  _status = STATUS.OFFLINE;
  _gen += 1;
  _dial = () => db.connect();
  // `auth.__resetForTests` drops every auth listener, this one too.
  followSession();
}
